<template>
  <div class="text-sm sm:text-base md:text-lg text-grey-darker flex justify-center">
    <HalfCircleSpinner
        v-show="!initiated"
        color="#0b5aaa"
        class="m-32"
    ></HalfCircleSpinner>
    <form-wizard
        v-if="initiated"
        shape="circle"
        :stepSize="deviceSize"
        color="#45AC34"
        error-color="#45AC34"
        @on-loading="setLoading"
        @on-error="handleErrorMessage"
        transition="slide in"
        :start-index="wizardStartIndex"
        class="w-full"
        ref="wizard"
    >
      <h2 slot="title"></h2>
      <tab-content
          title="Animal type"
          icon="ti-view-my ti-view-grid"
          :before-change="validateAnimal"
      >
        <animal
            :productGroup="currentMatrix.productGroup"
            @animalSelected="setSelectedAnimal"
            :selectedProductVersion="selectedProductVersion"
        ></animal>
      </tab-content>
      <tab-content
          title="Feed input"
          icon="ti-view-my ti-view-my-corn"
          :before-change="validatePhytate"
      >
        <feed-table
            :animal="currentMatrix.productGroup"
            :dietId="currentMatrix.diet && currentMatrix.diet.id ? currentMatrix.diet.id : undefined"
            :diets="diets[currentMatrix.productGroup]"
            :dietaryPhytate="currentMatrix.dietaryPhytate"
            :ingredientsArray="ingredients"
            @update:phytate="phytateUpdated"
            @update:diet="data => currentMatrix.diet=data.diet"
            @addItemToDiets="addItemToDiets"
            @update:diets="data => formatDiets(data)"
            @deleteLocalDiet="payload => deleteLocalDiet(payload.dietId)"
            @phytateValueManual="value => phytateValueManual = value"
        ></feed-table>
      </tab-content>
      <tab-content
          :title="selectedProductVersion === 'hiphos' ? 'HiPhos Form' : 'HiPhorius™ Form'"
          icon="ti-view-my ti-view-my-form"
          :before-change="validateProduct"
      >
        <product
            :suggested-dose="suggestedDose"
            @update:product="product => this.currentMatrix.productForm = product"
            :selectedProductVersion="selectedProductVersion"
            :animal="currentMatrix.productGroup"
            :product="currentMatrix.productForm"
        ></product>
      </tab-content>
      <tab-content
          title="Phytate Accessibility Factor"
          icon="ti-view-my ti-view-my-factor"
          :before-change="calculateAccessibility"
      >
        <accessibility
            :defaultValue="defaultAccessibility"
            :selectedAnimal="currentMatrix.productGroup"
            :totalPhytateP="currentMatrix.dietaryPhytate"
            :user="user"
            :selectedProductVersion="selectedProductVersion"
        ></accessibility>
      </tab-content>

      <tab-content
          title="Dig. Ca Calculator"
          icon="ti-view-my ti-view-my-ca-calculator"
          v-if="selectedProductVersion === 'hiphorius' && userCanSeeCalcium"
          :before-change="calculateMatrix"
      >
        <calcium
            :initial-phytate="phytateClear"
            :total-phytate-p="Number(currentMatrix.dietaryPhytate)"
            :dose-default="doseClear()"
            :diet-dose="currentMatrix.dose"
            :dietCa="currentMatrix.accessibilityProperties && currentMatrix.accessibilityProperties.dietCa ? currentMatrix.accessibilityProperties.dietCa : null"
            :useDefaultAccessibility="currentMatrix.accessibilityProperties && currentMatrix.accessibilityProperties.useDefault"
            :animal="currentMatrix.productGroup"
            :phosphateType="currentMatrix.phosphateType"
            @calciumUpdated="handleCalciumUpdate"
        />
      </tab-content>

      <tab-content
          title="DSM Nutrient Matrix"
          icon="ti-view-my ti-view-my-grid"
      >
        <matrix
            :rows="currentMatrix.nutrients"
            :matrixId="currentMatrix.id"
            :matrixName="currentMatrix.name"
            :phytate="currentMatrix.dietaryPhytate"
            :animal="currentMatrix.productGroup"
            :accessibility="currentMatrix.accessibility"
            :accessiblePhytateP="currentMatrix.accessiblePhytateP"
            :correctedPhytateP="currentMatrix.correctedPhytateP"
            :product="currentMatrix.productForm"
            :dose="currentMatrix.dose"
            :suggestedDose="currentMatrix.dietSuggestedDose"
            :exportModalActive.sync="exportModalActive"
            :exportWithProperties.sync="exportWithProperties"
            :showSaveMatrixModal.sync="showSaveMatrixModal"
            :dietName="currentMatrix != null && currentMatrix.diet != null ? currentMatrix.diet.name : null"
            :phosphateType="currentMatrix != null ? currentMatrix.phosphateType : null"
            :phosphatePrice="currentMatrix != null ? currentMatrix.phosphatePrice : null"
            :hiphosPrice="currentMatrix != null ? currentMatrix.hiphosPrice : null"
            :digestibility="currentMatrix != null ? currentMatrix.digestibility : null"
            :availability="currentMatrix != null ? currentMatrix.availability : null"
            :totalPhosphorus="currentMatrix != null ? currentMatrix.totalPhosphorus : null"
            :currentMatrix="currentMatrix"
            :calciumPhosphateType="currentMatrix && currentMatrix.calcium ? currentMatrix.calcium.phosphate : null"
            :selectedProductVersion="selectedProductVersion"
            :calculatedDefaultAccessibility="calculatedDefaultAccessibility"
            :isAqua="isAqua"
            @update:matrixProps="handleMatrixPropsUpdate"
        ></matrix>
      </tab-content>
      <div
          class="column is-8 is-offset-2 notification is-danger has-text-centered z-40"
          v-if="errorMsg"
      >
        {{errorMsg}}
      </div>
      <template
          slot="footer"
          slot-scope="props"
          class=""
      >
        <div class="fixed bottom-0 pin-r pin-l bg-brand-500 py-2 px-2 md:px-4 shadow left-0 right-0 z-50">
          <div class="container mx-auto px-6 py-2 flex justify-between">
            <div class="flex-1 flex justify-start items-center wizard-footer-left">
              <wizard-button
                  v-if="props.activeTabIndex > 0"
                  @click.native="props.prevTab()"
                  :style="{ backgroundColor: '#0b5aaa', color: 'white', padding: '1rem 1.5rem', border: 'none' }"
                  class="btn rounded-full text-sm md:text-base mt-2 md:mt-0"
              >Previous
              </wizard-button>
            </div>
            <div class="flex-1 flex justify-center items-center">
              <div v-if="selectedProductVersion === 'hiphorius'" class="text-brand-300 font-medium text-2xl lg:text-4xl">HiPhorius<span class="text-xs lg:text-sm align-text-top">™</span></div>
              <div v-if="selectedProductVersion === 'hiphos'" class="text-brand-300 font-medium text-2xl lg:text-3xl">RONOZYME<span class="text-xs lg:text-sm align-text-top">®</span> HiPhos</div>
            </div>
            <div class="flex-1 flex justify-end items-center wizard-footer-right">
              <wizard-button
                  v-show="props.isLastStep"
                  @click.native="showSaveMatrixModal = true"
                  :style="{ backgroundColor: 'white', color: 'black', padding: '1rem 1.5rem', border: 'none' }"
                  class="btn bg-grey-lighter btn-blank rounded-full text-grey-darkest text-sm md:text-base mt-2 md:mt-0 mr-4 lg:mr-6"
              >Save Calculation
              </wizard-button>

              <wizard-button
                  v-show="isCalciumStep(props) && isCalciumStepVisible "
                  @click.native="currentMatrix.calcium.skipStep = true; props.nextTab()"
                  :style="{ backgroundColor: 'white', color: 'black', padding: '1rem 1.5rem', border: 'none' }"
                  class="secondary btn bg-green-600 text-white rounded-full text-grey-darkest text-sm md:text-base mt-2 md:mt-0 mr-4 lg:mr-6"
              >Skip step
              </wizard-button>

              <wizard-button
                  v-if="!(props.isLastStep || (isCalciumStep(props) && currentMatrix.productGroup === 'Layers'))"
                  @click.native="handleNextClick(props)"
                  :style="{ backgroundColor: '#0b5aaa', color: 'white', padding: '1rem 1.5rem', border: 'none' }"
                  class="btn rounded-full text-sm md:text-base bg-white mt-2 md:mt-0"
                  v-text="isCalciumStep(props) && isCalciumStepVisible ? 'Proceed and use values' : 'Next'"
              >
              </wizard-button>

              <wizard-button
                  v-if="props.isLastStep"
                  @click.native="exportModalActive = true"
                  :style="{ backgroundColor: '#0b5aaa', color: 'white', padding: '1rem 1.5rem', border: 'none' }"
                  class="btn rounded-full text-sm md:text-base"
              >Export...
              </wizard-button>
            </div>
          </div>
        </div>
      </template>
    </form-wizard>
  </div>
</template>

<script>
import {bugsnagClient} from "../lib/bugsnag";
import {HalfCircleSpinner} from "epic-spinners";
import {PRODUCT_FORM_MAP, PRODUCT_FORM_MAP_DOSE as productFormMapDose} from "../utilities/application";

export default {
  components: {
    HalfCircleSpinner
  },
  props: ["passedMatrixData", "user", "selectedProductVersion"],
  data: () => ({
    // Use dynamic steps components to control the array and order of items in it https://jsfiddle.net/bt5dhqtf/973/
    form: new Form({
      productGroup: "",
      dose: "",
      productForm: "",
      steps: [1]
    }),
    defaultAccessibility: 0.8,
    calculatedDefaultAccessibility: 0,
    matrixData: [],
    submitted: false,
    loadingWizard: false,
    errorMsg: null,
    selectedFileFormat: null,
    exportModalActive: false,
    exportWithProperties: false,
    showSaveMatrixModal: false,
    currentMatrix: {
      id: 0,
      name: "",
      productForm: "",
      productGroup: "",
      dose: 0,
      doseInPPM: 0,
      dietSuggestedDose: 0,
      accessibility: 0.0,
      accessibilityProperties: {
        useDefault: true,
        useCustom: false,
      },
      dietaryPhytate: "",
      accessiblePhytateP: 0.0,
      correctedPhytateP: 0.0,
      initialCorrectedPhytateP: 0.0,
      diet: {
        id: "",
        name: "",
        ingredients: []
      },
      nutrients: [],
      digestibility: 0,
      phosphateType: "",
      phosphatePrice: 0,
      hiphosPrice: 0,
      availability: 0,
      totalPhosphorus: 0,
      calcium: {}
    },
    ingredients: [],
    diets: {},
    initiated: false,
    phytateValueManual: false
  }),
  mounted: function () {
    EventHub.$on("correctedPhytatePUpdated", correctedPhytateP => {
      this.currentMatrix.correctedPhytateP = correctedPhytateP;
      this.currentMatrix.initialCorrectedPhytateP = correctedPhytateP;
    });
    EventHub.$on("accessiblePhytatePUpdated", accessiblePhytateP => {
      this.currentMatrix.accessiblePhytateP = accessiblePhytateP;
    });
    EventHub.$on(
        "correctedAccessibilityUpdated",
        correctedAccessibility => {
          if (this.calculatedDefaultAccessibility === 0) {
            this.calculatedDefaultAccessibility = correctedAccessibility;
          }
          this.currentMatrix.accessibility = correctedAccessibility;
        }
    );
    EventHub.$on(
        "defaultCalculatedAccessibilityUpdated",
        defaultCorrectedAccessibility => {
          this.calculatedDefaultAccessibility = defaultCorrectedAccessibility;
        }
    );
    EventHub.$on(
        "accessibilityPropertiesUpdated",
        payload => {
          this.currentMatrix.accessibilityProperties = {
            ...this.currentMatrix.accessibilityProperties,
            ...payload
          };
        }
    );
    EventHub.$on("exportMatrix", payload => {
      this.exportMatrix(payload.matrixInPctg, payload.selectedUnit);
    });
    EventHub.$on("fileFormatUpdated", selectedFileFormat => {
      this.selectedFileFormat = selectedFileFormat;
    });
    EventHub.$on("saveMatrix", payload => {
      this.currentMatrix.name = payload.matrixName;
      if (payload.isNew || this.currentMatrix.id == 0) {
        this.createMatrix();
      } else {
        this.saveMatrix();
      }
    });
    EventHub.$on("saveDiet", this.handleSaveDiet);
    bugsnagClient.notify(new Error());
  },

  beforeCreate: async function () {
    await axios
        .get("/ingredients")
        .then(response => {
          this.ingredients = response.data;
        })
        .catch(error => {
          this.$toasted.global.calc_error({
            error,
            additionalMessage: "Please try refreshing the page."
          });
          bugsnagClient.notify(error);
        });

    await axios
        .get("/diets")
        .then(response => this.formatDiets(response.data.diets))
        .then(currentDietId => {
        })
        .catch(error => {
          this.$toasted.global.calc_error({
            error,
            additionalMessage: "Please try refreshing the page."
          });
          bugsnagClient.notify(error);
        });
    await this.init();
  },

  created() {
    //post init
    this.currentMatrix.doseInPPM = this.doseInPPM
    this.form.productForm = this.selectedProductVersion === 'hiphorius' ? "HiPhorius10 (10,000 FYT/g)" : "HiPhos 10,000 GT"
    this.currentMatrix.productForm = this.selectedProductVersion === 'hiphorius' ? "HiPhorius10 (10,000 FYT/g)" : "HiPhos 10,000 GT"
  },

  computed: {
    deviceSize() {
      if (this.$mq === "mobile") {
        return "xs";
      } else if (this.$mq === "tablet") {
        return "sm";
      } else {
        return "md";
      }
    },

    suggestedDose() {
      return _.round(this.currentMatrix.dose / 50, 0) * 50;
    },

    initialMatrixArray() {
      return this.passedMatrixData
          ? JSON.parse(this.passedMatrixData)
          : [];
    },

    wizardStartIndex() {
      return this.currentMatrix.id == 0 ? 0 : this.currentMatrix.productGroup === 'Broiler' && this.selectedProductVersion === 'hiphorius' ? 5 : 4; 
    },

    doseInPPM() {
      return this.currentMatrix.dose && this.currentMatrix.productForm && productFormMapDose[this.selectedProductVersion][this.currentMatrix.productForm] 
        ? _.round((this.currentMatrix.dose / productFormMapDose[this.selectedProductVersion][this.currentMatrix.productForm]) *1000,2)
        : 0;
    },

    phytateClear() {
      return this.currentMatrix.initialCorrectedPhytateP ? _.round(this.currentMatrix.initialCorrectedPhytateP, 3) : 0;
    },

    userCanSeeCalcium() {
      return this.user.type == 'Super Admin' || this.user.type == 'Administrator' || this.user.type == 'Advanced + Dig. Ca';
    },

    isAqua() {
      return this.currentMatrix.productGroup === 'Seabream' || this.currentMatrix.productGroup === 'Tilapia' || this.currentMatrix.productGroup === 'Trout' ;
    },

    isCalciumStepVisible() {
      return this.currentMatrix.productGroup !== 'Layer' && !this.isAqua && this.userCanSeeCalcium
    }
  },

  watch: {
    doseInPPM() {
      this.currentMatrix.doseInPPM = this.doseInPPM
    },
  },

  methods: {
    isCalciumStep(props) {
      return props.activeTabIndex === 4 && !props.isLastStep
    },
    doseClear() {
      return Number(this.currentMatrix.dietSuggestedDose);
    },
    init: async function () {
      if (this.initialMatrixArray.length == 0) {
        this.initiated = true;
        return;
      }
      try {
        this.currentMatrix = this.initialMatrixArray[0];
        this.currentMatrix.initialCorrectedPhytateP = this.initialMatrixArray[0].correctedPhytateP;
        this.currentMatrix.calcium = this.initialMatrixArray[0].calcium ? { ...this.initialMatrixArray[0].calcium, skipStep: true} : { skipStep: true };
        this.currentMatrix.dietSuggestedDose = this.currentMatrix
            .dietSuggestedDose
            ? this.currentMatrix.dietSuggestedDose
            : this.currentMatrix.dose;
        this.currentMatrix.nutrients = this.currentMatrix.nutrients.map(
            row => {
              return {
                ...row,
                contribution: row.pivot.contribution,
                nutrient_id: row.pivot.nutrient_id,
                unit: row.pivot.unit,
                value: row.pivot.value
              };
            }
        );
        this.currentMatrix.productForm = this.initialMatrixArray[0].productForm ? this.initialMatrixArray[0].productForm : "HiPhos 10,000 GT";
        this.submitted = true;
        this.initiated = true;
      } catch (error) {
        this.$toasted.global.calc_error({
          error,
          additionalMessage: `Failed to load matrix calculation`
        });
        bugsnagClient.notify(error);
      }
      return;
    },
    setLoading: function (value) {
      this.loadingWizard = value;
    },
    handleErrorMessage: function (errorMsg) {
      this.errorMsg = errorMsg;
    },

    formatDiets(newDiets, currentDietId) {
      let groupedDiets = _.chain(newDiets)
          .map(diet => ({
            ...diet,
            id: _.toString(diet.id),
            edited: false
          }))
          .groupBy("productGroup")
          .value();
      this.diets = _.mapValues(groupedDiets, animalDiets => {
        return _.keyBy(animalDiets, diet => _.toString(diet.id));
      });
      return Promise.resolve(currentDietId);
    },

    handleSaveDiet: async function () {
      if (
          this.currentMatrix.diet &&
          this.currentMatrix.diet.id &&
          !_.toString(this.currentMatrix.diet.id).includes("temp")
      ) {
        await this.saveDiet();
      } else {
        await this.createDiet();
      }
      return;
    },

    createDiet: async function () {
      var self = this;
      return axios
          .post("/createDiet", {
            diet: self.currentMatrix.diet
          })
          .then(response =>
              self.formatDiets(
                  response.data.diets,
                  response.data.currentDietId
              )
          )
          .then(currentDietId => {
            self.currentMatrix.diet =
                self.diets[self.currentMatrix.productGroup][
                    currentDietId
                    ];
            self.currentMatrix.diet.edited = false;
            self.currentMatrix.diet_id = currentDietId;
            self.$toasted.global.calc_success({
              mainMessage: `Diet '${self.currentMatrix.diet.name}' saved successfully!`
            });
          })
          .catch(error => {
            self.$toasted.global.calc_error({
              error,
              mainMessage: `Diet '${self.currentMatrix.diet.name}' couldn not be saved.`,
              additionalMessage: _.get(error, "response.data.message")
                  ? error.response.data.message
                  : ""
            });
            bugsnagClient.notify(error);
          });
    },

    saveDiet: async function () {
      var self = this;
      return axios
          .post("/saveDiet", {
            diet: self.currentMatrix.diet
          })
          .then(response => {
            self.$toasted.global.calc_success({
              mainMessage: `Diet ${self.currentMatrix.diet.name} saved successfully!`
            });
            self.currentMatrix.diet.edited = false;
          })
          .catch(error => {
            self.$toasted.global.calc_error({
              error,
              additionalMessage: `Diet ${self.currentMatrix.diet.name} couldn not be saved.`
            });
            bugsnagClient.notify(error);
          });
    },

    validateAnimal: function () {
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          if (this.currentMatrix.productGroup == "") {
            this.form.steps.push(2);
            reject("Please select an animal type to proceed.");
          } else {
            if (this.currentMatrix.accessibility == 0) {
              this.currentMatrix.accessibility = this.calculatedDefaultAccessibility;
            }
            this.form.steps.push(3);
            resolve(true);
          }
        }, 500);
      });
    },
    validateProduct: function () {
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          if (this.currentMatrix.productForm == "") {
            EventHub.$emit("invalidProduct");
            this.form.steps.push(3);
            reject("");
          } else {
            this.form.steps.push(4);
            resolve(true);
          }
        }, 500);
      });
    },
    setSelectedAnimal: async function (selectedAnimal) {
      await this.resetCurrentMatrix();
      this.currentMatrix.productGroup = selectedAnimal;
      this.errorMsg = "";
      if (this.submitted && this.currentMatrix.correctedPhytateP) {
        this.calculateAccessibility();
      }
    },
    resetCurrentMatrix() {
      this.currentMatrix.diet = {
        id: "",
        name: "",
        ingredients: []
      };
      this.currentMatrix.dose = 0;
      this.currentMatrix.dietSuggestedDose = 0;
      this.currentMatrix.accessibility = 0.0;
      this.currentMatrix.dietaryPhytate = "";
      this.currentMatrix.correctedPhytateP = 0;
      this.currentMatrix.initialCorrectedPhytateP = 0;
      this.currentMatrix.accessiblePhytateP = 0;
      this.currentMatrix.nutrients = [];
    },
    setPhytase: function () {
      this.currentMatrix.dietaryPhytate;
    },
    calculate: function () {
      axios
          .post("/matrix", {
            productGroup: this.currentMatrix.productGroup,
            productForm: this.currentMatrix.productForm,
            dose: this.currentMatrix.dose,
            digestibility: this.currentMatrix.digestibility,
            calcium: this.currentMatrix.calcium
          })
          .then(
              response => (this.currentMatrix.nutrients = response.data)
          )
          .catch(error => {
            this.$toasted.global.calc_error({
              error,
              additionalMessage: `Matrix could not be calculated.`
            });
            bugsnagClient.notify(error);
            return false;
          });

      this.submitted = true;
      return true;
    },
    recalculate: function () {
      if (this.submitted && this.currentMatrix && this.currentMatrix.dose) {
        this.calculate();
      }

      return true;
    },
    phytateUpdated: function (data) {
      this.currentMatrix.dietaryPhytate = data.phytate;
      EventHub.$emit("phytateUpdated");
    },

    validatePhytate: function () {
      const _this = this;
      return new Promise((resolve, reject) => {
        if (_this.currentMatrix.diet && _this.currentMatrix.diet.id != "" && !_this.phytateValueManual) {
          let pctgSum = _this.currentMatrix.diet.ingredients.reduce(
              (pctg, ingr) => pctg + parseFloat(ingr.percentage),
              0
          );
          if (pctgSum != 100) {
            reject("Total inclusion percentage needs to be 100%.");
          }
        }
        if (_this.currentMatrix.dietaryPhytate === "") {
          reject(
              "Please select a diet or enter your dietary Phytate-P value manually"
          );
        } else if (_this.currentMatrix.dietaryPhytate === 0) {
          reject(
              "Phytate-P value is zero. Please add ingredients to your diet or enter a non-zero custom Phytate-P value."
          );
        } else {
          this.currentMatrix.initialCorrectedPhytateP = this.currentMatrix.correctedPhytateP
          resolve(true);
        }
      });
    },

    calculateAccessibility: function () {
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          axios
              .post("/calculateDose", {
                productGroup: this.currentMatrix.productGroup,
                dietaryPhytate: this.currentMatrix.correctedPhytateP
              })
              .then(response => {
                this.currentMatrix.dose = response.data;
                EventHub.$emit("update:dose", {
                  dose: response.data
                });
                this.currentMatrix.dietSuggestedDose = response.data;
                this.currentMatrix.doseInPPM = this.doseInPPM
              })
              .then(value => {
                if(this.selectedProductVersion === 'hiphorius') {
                  if(this.currentMatrix.productGroup === 'Layer' || this.isAqua || !this.userCanSeeCalcium) {
                    // skip calcium
                    this.currentMatrix.calcium.skipStep = true
                    this.calculate();
                    if ((this.currentMatrix.productGroup === 'Layer' || this.isAqua) && this.userCanSeeCalcium) {
                      this.$refs.wizard.changeTab(3,5)
                    }
                    resolve(true)
                  }
                  EventHub.$emit("calculateCalcium");
                  resolve(true)
                } else {
                  this.calculate();
                  resolve(true)
                }
              })
              .catch(error => {
                this.$toasted.global.calc_error({
                  error,
                  additionalMessage: `Matrix could not be calculated.`
                });
                bugsnagClient.notify(error);
                reject("Oops something went wrong");
              });
        }, 500);
      });
    },

    createMatrix: async function () {
      if (this.currentMatrix.diet.edited) {
        await this.handleSaveDiet();
      }
      axios
          .post("/createMatrix", {
            diet: this.currentMatrix.diet,
            productGroup: this.currentMatrix.productGroup,
            productForm: this.currentMatrix.productForm,
            dietaryPhytate: this.currentMatrix.dietaryPhytate,
            accessiblePhytateP: this.currentMatrix.accessiblePhytateP,
            correctedPhytateP: this.currentMatrix.correctedPhytateP,
            dose: this.currentMatrix.dose,
            dietSuggestedDose: this.currentMatrix.dietSuggestedDose,
            accessibility: this.currentMatrix.accessibility,
            matrix: this.currentMatrix.nutrients,
            name: this.currentMatrix.name,
            calcium: this.currentMatrix.calcium,
            accessibilityProperties: this.currentMatrix.accessibilityProperties,
          })
          .then(response => {
            this.currentMatrix.id = response.data.matrixId;
            this.currentMatrix.name = response.data.matrixName;
            this.$toasted.global.calc_success({
              mainMessage: `Calculation '${this.currentMatrix.name}' created successfully!`
            });
          })
          .catch(error => {
            this.$toasted.global.calc_error({
              error,
              additionalMessage: `Calculation '${this.currentMatrix.name}' couldn not be created. `
            });
            bugsnagClient.notify(error);
          });
    },

    saveMatrix: async function () {
      if (this.currentMatrix.diet && this.currentMatrix.diet.edited) {
        await this.handleSaveDiet();
      }
      axios
          .post("/saveMatrix", {
            diet: this.currentMatrix.diet,
            productGroup: this.currentMatrix.productGroup,
            productForm: this.currentMatrix.productForm,
            dietaryPhytate: this.currentMatrix.dietaryPhytate,
            accessiblePhytateP: this.currentMatrix.accessiblePhytateP,
            correctedPhytateP: this.currentMatrix.correctedPhytateP,
            dose: this.currentMatrix.dose,
            dietSuggestedDose: this.currentMatrix.dietSuggestedDose,
            accessibility: this.currentMatrix.accessibility,
            matrix: this.currentMatrix.nutrients,
            name: this.currentMatrix.name,
            id: this.currentMatrix.id,
            calcium: this.currentMatrix.calcium,
            accessibilityProperties: this.currentMatrix.accessibilityProperties,
          })
          .then(response => {
            this.$toasted.global.calc_success({
              mainMessage: `Calculation '${this.currentMatrix.name}' saved successfully!`
            });
          })
          .catch(error => {
            this.$toasted.global.calc_error({
              error,
              additionalMessage: `Calculation '${this.currentMatrix.name}' couldn not be saved.`
            });
            bugsnagClient.notify(error);
          });
    },

    exportMatrix: function (matrixInPctg, selectedUnit) {
      if (this.submitted) {
        axios({
          method: "post",
          url: "/exportMatrix",
          data: {
            matrix_data:
                selectedUnit == "%"
                    ? matrixInPctg
                    : this.currentMatrix.nutrients,
            fileFormat: this.selectedFileFormat,
            withProperties: this.exportWithProperties,
            properties: {
              phytate: this.currentMatrix.dietaryPhytate,
              animal: this.currentMatrix.productGroup,
              accessibility: this.currentMatrix.accessibility,
              accessiblePhytateP: this.currentMatrix
                  .accessiblePhytateP,
              correctedPhytateP: this.currentMatrix
                  .correctedPhytateP,
              product: this.currentMatrix.productForm,
              dose: this.currentMatrix.dose,
              dietSuggestedDose: this.currentMatrix
                  .dietSuggestedDose,
              doseInPPM: this.currentMatrix.doseInPPM,
              dietName: this.currentMatrix.diet != null ? this.currentMatrix.diet.name : null
            }
          },
          responseType: "blob",
          headers: {
            Accept:
                "text/csv, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          }
        })
            .then(response => {
              let blob = new Blob([response.data]);
              if (!window.navigator.msSaveOrOpenBlob) {
                var blobURL = window.URL.createObjectURL(
                    new Blob([response.data])
                );
                var tempLink = document.createElement("a");
                tempLink.style.display = "none";
                tempLink.href = blobURL;
                tempLink.setAttribute(
                    "download",
                    `matrix.${this.selectedFileFormat}`
                );

                // Safari thinks _blank anchor are pop ups. We only want to set _blank
                // target if the browser does not support the HTML5 download attribute.
                // This allows you to download files in desktop safari if pop up blocking
                // is enabled.
                if (typeof tempLink.download === "undefined") {
                  tempLink.setAttribute("target", "_blank");
                }

                document.body.appendChild(tempLink);
                tempLink.click();
                document.body.removeChild(tempLink);
                window.URL.revokeObjectURL(blobURL);
              } else {
                window.navigator.msSaveOrOpenBlob(
                    new Blob([response.data]),
                    `matrix.${this.selectedFileFormat}`
                );
              }
            })
            .catch(function (error) {
              bugsnagClient.notify(error);
            });
      }
    },

    handleMatrixPropsUpdate(payload) {
      if (payload.type == "phytate") {
        this.currentMatrix.dietaryPhytate = payload.value;
        this.currentMatrix.accessiblePhytateP =
            payload.value * this.defaultAccessibility;
        this.currentMatrix.correctedPhytateP =
            payload.value * this.currentMatrix.accessibility;
        this.calculateAccessibility()
            .then(data => {
            })
            .catch(error => {
              bugsnagClient.notify(error);
            });
      } else if (payload.type == "dose" && payload.value != 0) {
        this.currentMatrix.dose = payload.doseInFYT;
        this.currentMatrix.doseInPPM = payload.doseInPPM;
        EventHub.$emit("update:dose", {
          dose: payload.doseInFYT
        });
        this.recalculate();
      } else if (payload.type == "animal") {
        this.setSelectedAnimal(payload.value);
      } else if (payload.type == "product") {
        this.currentMatrix.productForm = payload.value;
        this.recalculate();
      } else if (payload.type == "digestibility") {
        this.currentMatrix.digestibility = payload.value;
        this.recalculate();
      } else if (payload.type === "phosphateType") {
        this.currentMatrix.phosphateType = payload.value;
        this.currentMatrix.calcium.phosphate = payload.value;
        this.recalculate();
      } else {
        this.currentMatrix[payload.type] = payload.value;
      }
    },

    addItemToDiets(diet) {
      if (this.diets[diet.productGroup]) {
        this.diets[diet.productGroup][diet.id] = diet;
      } else {
        this.diets[diet.productGroup] = {};
        this.diets[diet.productGroup][diet.id] = diet;
      }
    },

    deleteLocalDiet(dietId) {
      if (_.has(this.diets, [this.currentMatrix.productGroup, dietId])) {
        delete this.diets[this.currentMatrix.productGroup][dietId];
      }
    },

    calculateMatrix() {
      return new Promise((resolve, reject) => {
        if(this.calculate()) resolve(true);
        reject("Oops, something went wrong while calculating the matrix.");
      });
    },

    handleCalciumUpdate(payload) {
      this.currentMatrix.calcium = {
        ...this.currentMatrix.calcium,
        ...payload
      };
      if(payload.totalCa) {
        // calculate accessible phytateP again and send calculateDose sever request
      }
    },

    handleNextClick(props) {
      this.currentMatrix.calcium.skipStep = false; 
      // TODO: commit calcium props as current if calcium step is active
      if(this.isCalciumStep(props)) {
        let customAccessibility = this.currentMatrix.calcium.phytate / this.currentMatrix.dietaryPhytate;
        if (customAccessibility !== this.currentMatrix.accessibility) {
          this.currentMatrix.accessibility = this.currentMatrix.calcium.phytate / this.currentMatrix.dietaryPhytate;
          this.currentMatrix.accessibilityProperties.useCustom = true
        }
        // this.currentMatrix.accessiblePhytateP = this.currentMatrix.dietaryPhytate * this.defaultAccessibility;
        this.currentMatrix.correctedPhytateP = this.currentMatrix.calcium.phytate
        this.currentMatrix.dose = this.currentMatrix.calcium.dose
        this.currentMatrix.doseInPPM = this.doseInPPM;
      }
      props.nextTab();
    }
  }
};
</script>
<style>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}

.fade-enter,
.fade-leave-to
  /* .fade-leave-active below version 2.1.8 */

{
  opacity: 0;
}

.wizard-icon-container {
  border-radius: 50% !important;
}

.vue-form-wizard .wizard-icon-circle {
  padding: 5px;
  width: 40px !important;
  height: 40px !important;
  border-color: white !important;
  background-color: #fff !important;
  z-index: 10;
}

@media (min-width: 768px) {
  .vue-form-wizard .wizard-icon-circle {
    width: 58px !important;
    height: 58px !important;
  }
}
@media (min-width: 1280px) {
  .vue-form-wizard .wizard-icon-circle {
    width: 65px !important;
    height: 65px !important;
  }
}
@media (min-width: 1536px) {
  .vue-form-wizard .wizard-icon-circle {
    width: 70px !important;
    height: 70px !important;
  }
}

.vue-form-wizard li.active .wizard-icon-circle {
  box-shadow: 0px 0px 19px rgb(171 177 180 / 39%);
}

.vue-form-wizard .wizard-navigation .wizard-progress-with-circle {
  top: 53px !important;
}

.wizard-progress-with-circle {
  display: none;
}

.wizard-nav.wizard-nav-pills {
  display: none;
}

.vue-form-wizard .wizard-nav-pills li:after {
  content: "•••••";
  color: #E2E9F0;
  position: absolute;
  /*width: 100%;*/
  /*display: inline-block;*/
  font-size: 40px;
  top: 22px;
  right: 0;
  left: 80%;
}

@media (min-width: 1024px) {
  .vue-form-wizard .wizard-nav-pills li:after {
    left: 78%;
  }
}
@media (min-width: 1280px) {
  .vue-form-wizard .wizard-nav-pills li:after {
    left: 83%;
  }
}
@media (min-width: 1536px) {
  .vue-form-wizard .wizard-nav-pills li:after {
    left: 85%;
  }
}

@media (max-width: 600px) {
  .vue-form-wizard .wizard-nav-pills li:after {
    content: "";
  }

  .ti-view-my {
    height: 16px !important;
    width: 16px !important;
    font-size: 18px !important;
  }

  .vue-form-wizard .wizard-icon-circle {
    padding: 0;
  }

  .vue-form-wizard .stepTitle {
    white-space: nowrap;
  }
  /* TODO implement mobile view for tabs */

  .vue-form-wizard .wizard-nav-pills li:not(.active) .checked .ti-view-grid:before,
  .vue-form-wizard .wizard-nav-pills li:not(.active) .checked .ti-view-my-corn:before,
  .vue-form-wizard .wizard-nav-pills li:not(.active) .checked .ti-view-my-form:before,
  .vue-form-wizard .wizard-nav-pills li:not(.active) .checked .ti-view-my-factor:before,
  .vue-form-wizard .wizard-nav-pills li:not(.active) .checked .ti-view-my-grid:before {
    font-size: medium;
  }
}

@media (max-width: 820px) {
  .vue-form-wizard .wizard-nav-pills li:after {
    content: "";
  }

  .ti-view-my {
    height: 16px !important;
    width: 16px !important;
    font-size: 18px !important;
  }

  .vue-form-wizard .wizard-icon-circle {
    padding: 0;
  }

  .vue-form-wizard .stepTitle {
    white-space: normal;
  }

  .vue-form-wizard .wizard-nav-pills li:not(.active) .checked .ti-view-grid:before,
  .vue-form-wizard .wizard-nav-pills li:not(.active) .checked .ti-view-my-corn:before,
  .vue-form-wizard .wizard-nav-pills li:not(.active) .checked .ti-view-my-form:before,
  .vue-form-wizard .wizard-nav-pills li:not(.active) .checked .ti-view-my-factor:before,
  .vue-form-wizard .wizard-nav-pills li:not(.active) .checked .ti-view-my-grid:before {
    font-size: medium;
  }

  .vue-form-wizard .wizard-nav-pills a, .vue-form-wizard .wizard-nav-pills li {
    align-items: flex-start;
    display: flex;
  }
}

.vue-form-wizard .wizard-nav-pills li:last-of-type::after {
  content: "";
}

.vue-form-wizard .wizard-nav-pills li:not(.active) .checked .ti-view-grid:before,
.vue-form-wizard .wizard-nav-pills li:not(.active) .checked .ti-view-my-corn:before,
.vue-form-wizard .wizard-nav-pills li:not(.active) .checked .ti-view-my-form:before,
.vue-form-wizard .wizard-nav-pills li:not(.active) .checked .ti-view-my-factor:before,
.vue-form-wizard .wizard-nav-pills li:not(.active) .checked .ti-view-my-grid:before {
  content: "\e64c" !important;
  color: #FFFFFF;
  font-weight: 900;
  font-size: xx-large;
}

.vue-form-wizard .wizard-nav-pills li:not(.active) .checked .ti-view-my-corn,
.vue-form-wizard .wizard-nav-pills li:not(.active) .checked .ti-view-my-form,
.vue-form-wizard .wizard-nav-pills li:not(.active) .checked .ti-view-my-factor,
.vue-form-wizard .wizard-nav-pills li:not(.active) .checked .ti-view-my-grid {
  background-image: none;
}

.ti-view-my {
  background-size: contain;
  background-repeat: no-repeat;
  background-position-y: center;
  height: 32px;
  width: 32px;
  margin: auto;
  font-size: 18px !important;
}

.ti-view-my-corn {
  background-image: url('/images/corn.svg');
}

.ti-view-my-form {
  background-image: url('/images/form.svg');
  height: 30px;
  width: 22px;
}

.ti-view-my-factor {
  background-image: url('/images/factor.svg');
  height: 30px;
  width: 22px;
}

.ti-view-my-grid {
  background-image: url('/images/grid.svg');
  height: 30px;
  width: 22px;
}

.ti-view-my-ca-calculator {
  background-image: url('/images/ca-calculator.svg');
  height: 30px;
  width: 22px;
}

.vue-form-wizard .wizard-nav-pills li:not(.active) .wizard-icon-circle:not(.checked) {
  background-color: #DFE7F4 !important;
  border: 0;
}

.vue-form-wizard li:not(.active) .wizard-icon-circle.checked {
  background-color: #45AC34 !important;
}

.vue-form-wizard .stepTitle {
  margin-top: 17px;
}

.vue-form-wizard .stepTitle.active {
  font-family: Fira Sans;
  font-style: normal;
  font-weight: 500;
  display: flex;
  align-items: center;
  text-align: center;
  color: #000000 !important;
}

@media (max-width:767px) {
  .vue-form-wizard .wizard-nav-pills li:not(.active) .checked .ti-view-grid:before,
  .vue-form-wizard .wizard-nav-pills li:not(.active) .checked .ti-view-my-corn:before,
  .vue-form-wizard .wizard-nav-pills li:not(.active) .checked .ti-view-my-form:before,
  .vue-form-wizard .wizard-nav-pills li:not(.active) .checked .ti-view-my-factor:before,
  .vue-form-wizard .wizard-nav-pills li:not(.active) .checked .ti-view-my-grid:before {
    font-size: 20px;
  }
}

</style>
